import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { inbox, linkedin, ai } from '../../lib/api';
import { parseApiDate } from '../../lib/format';
import { getIntlLocale } from '../../i18n/runtime';
import { useAuth } from '../../contexts/AuthContext';
import {
  Search,
  RefreshCw,
  Send,
  Sparkles,
  Paperclip,
  Smile,
  MoreHorizontal,
  AlertCircle,
  Loader2,
  Link2,
  Inbox as InboxIcon,
  Tag,
  Star,
  Trash2,
  UserPlus,
  X,
  Wand2,
  Image as ImageIcon,
  ChevronLeft,
} from 'lucide-react';

const EMOJIS = ['👍', '🎉', '🙏', '🙌', '🔥', '💡', '✅', '❤️', '😊', '😂', '🤝', '🚀', '👏', '⭐', '💪', '💼', '📩', '🗓', '👀', '🧠', '📈', '🙋‍♂️', '😉', '👋'];

export default function Inbox() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncNotice, setSyncNotice] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [draftingReply, setDraftingReply] = useState(false);
  const [draftNotice, setDraftNotice] = useState('');
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [linkedInConnected, setLinkedInConnected] = useState(false);
  const [linkedinConnecting, setLinkedinConnecting] = useState(false);
  const [linkedinConnectError, setLinkedinConnectError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [viewerAttachment, setViewerAttachment] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const menuRef = useRef(null);
  const emojiRef = useRef(null);
  const listRef = useRef(null);
  const resolvedDeepLinkRef = useRef(null);
  const deepLinkedConversationId = searchParams.get('conversation_id');

  const loadConversations = useCallback(async (opts = {}) => {
    const requestedPage = opts.page || 1;
    try {
      if (requestedPage === 1) setLoading(true);
      else setLoadingMore(true);
      const data = await inbox.list({ filter, search, page: requestedPage, limit: 25 });
      setConversations((prev) => (requestedPage === 1 ? data.conversations : [...prev, ...data.conversations]));
      setHasMore(requestedPage < (data.pages || 1));
      setPage(requestedPage);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filter, search]);

  const checkLinkedInConnection = useCallback(async () => {
    try {
      const { accounts } = await linkedin.getAccounts();
      setLinkedInConnected(accounts.length > 0);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadAutoReplyState = useCallback(async () => {
    try {
      const { settings } = await ai.getSettings();
      setAutoReplyEnabled(!!settings?.enabled);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadConversations({ page: 1 });
    checkLinkedInConnection();
    loadAutoReplyState();
  }, [loadConversations, checkLinkedInConnection, loadAutoReplyState]);

  // Trigger a fresh server-side sync when the inbox first opens, so brand new
  // LinkedIn messages show up immediately (not just up to last cron run).
  useEffect(() => {
    if (!linkedInConnected) return;
    inbox.sync().then(() => loadConversations({ page: 1 })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedInConnected]);

  useEffect(() => {
    if (!linkedInConnected) return;
    const id = setInterval(() => loadConversations({ page: 1 }), 60000);
    return () => clearInterval(id);
  }, [linkedInConnected, loadConversations]);

  useEffect(() => {
    if (!selectedConv?.id) return undefined;
    const id = setInterval(() => {
      loadMessages(selectedConv.id).catch(() => {});
      loadConversations({ page: 1 }).catch(() => {});
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConv?.id]);

  useEffect(() => {
    if (selectedConv) loadMessages(selectedConv.id);
    // Bug B fix: each conversation owns its own draft; clear the composer
    // when the user switches threads so a half-typed message doesn't bleed
    // across to a different contact.
    setNewMessage('');
    setPendingAttachment(null);
  }, [selectedConv]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!deepLinkedConversationId || resolvedDeepLinkRef.current === deepLinkedConversationId) return;
    const existing = conversations.find((conv) => conv.id === deepLinkedConversationId);
    if (existing) {
      resolvedDeepLinkRef.current = deepLinkedConversationId;
      setSelectedConv(existing);
      return;
    }
    let active = true;
    inbox.getConversation(deepLinkedConversationId)
      .then((conversation) => {
        if (!active || !conversation?.id) return;
        resolvedDeepLinkRef.current = deepLinkedConversationId;
        setConversations((prev) => (prev.some((conv) => conv.id === conversation.id) ? prev : [conversation, ...prev]));
        setSelectedConv(conversation);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [conversations, deepLinkedConversationId]);

  useEffect(() => {
    // Oldest-first ordering: newest message is at the BOTTOM of the
    // scroll container, so jump to the bottom when a conversation opens
    // (standard chat UX — users land on the most recent message).
    const container = messagesEndRef.current?.parentElement;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages]);

  useEffect(() => {
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setEmojiOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const onListScroll = (e) => {
    const el = e.currentTarget;
    if (loadingMore || !hasMore || loading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 160) {
      loadConversations({ page: page + 1 });
    }
  };

  const loadMessages = async (convId) => {
    try {
      setMessagesLoading(true);
      const data = await inbox.getMessages(convId);
      setMessages(data.messages);
      setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, unread_count: 0 } : c)));
    } catch (err) {
      console.error(err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSync = async (deep = false) => {
    try {
      setSyncing(true);
      setSyncNotice('');
      const result = await inbox.sync(deep);
      await loadConversations({ page: 1 });
      if (selectedConv?.id) {
        await loadMessages(selectedConv.id);
      }
      const summary = result?.summary;
      if (summary) {
        const errors = summary.errors || [];
        const inserted = summary.messages_inserted || 0;
        const seen = summary.messages_seen || 0;
        const aiScheduled = summary.ai_scheduled || 0;
        const aiPostponed = summary.ai_postponed || 0;
        const aiQueued = summary.ai_queued || 0;
        const aiSent = summary.ai_sent || 0;
        const aiSkipped = summary.ai_skipped || 0;
        const firstAiSkip = (summary.ai_skip_reasons || [])[0];
        if (errors.length) {
          setSyncNotice(`Sync warning: ${errors[0]}`);
        } else {
          const aiBits = [];
          if (aiScheduled) aiBits.push(`${aiScheduled} scheduled`);
          if (aiPostponed) aiBits.push(`${aiPostponed} postponed`);
          if (aiQueued) aiBits.push(`${aiQueued} queued`);
          if (aiSent) aiBits.push(`${aiSent} sent`);
          if (aiSkipped) aiBits.push(`${aiSkipped} skipped`);
          const aiSuffix = aiBits.length ? ` AI: ${aiBits.join(', ')}.` : '';
          const aiReasonSuffix = firstAiSkip?.reason ? ` Reason: ${firstAiSkip.reason}.` : '';
          setSyncNotice(`Sync OK: ${seen} messages checked, ${inserted} new.${aiSuffix}${aiReasonSuffix}`);
        }
      } else if (result?.status === 'running') {
        setSyncNotice('Full history sync is running in the backend.');
      }
    } catch (err) {
      console.error(err);
      setSyncNotice(err.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSend = async () => {
    if (!selectedConv) return;
    const text = newMessage.trim();
    // Need at least one of: text, pending file
    if (!text && !pendingAttachment) return;
    try {
      setSending(true);
      if (pendingAttachment) {
        // Send file (with optional text caption) in one Unipile call.
        await inbox.attach(selectedConv.id, pendingAttachment, text);
      } else {
        await inbox.sendMessage(selectedConv.id, text, false);
      }
      setNewMessage('');
      setPendingAttachment(null);
      await loadMessages(selectedConv.id);
    } catch (err) {
      console.error(err);
      // eslint-disable-next-line no-alert
      alert(err.message || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleRewrite = async () => {
    const text = newMessage.trim();
    if (!text || rewriting) return;
    try {
      setRewriting(true);
      const { rewritten } = await ai.rewrite(text, 'professional');
      if (rewritten) setNewMessage(rewritten);
      setTimeout(() => textareaRef.current?.focus(), 0);
    } catch (err) {
      const msg = err?.message || 'Rewrite failed';
      if (msg.toLowerCase().includes('openai')) {
        alert('Please add your OpenAI API key in Settings first.');
      } else {
        alert(msg);
      }
      console.error(err);
    } finally {
      setRewriting(false);
    }
  };

  const handleGenerateReplyDraft = async () => {
    if (!selectedConv || draftingReply) return;
    try {
      setDraftingReply(true);
      const res = await ai.replyDraft(selectedConv.id);
      const draft = res?.draft || res?.approval?.draft;
      if (draft) setNewMessage(draft);
      setDraftNotice(res?.queued_for_approval ? 'Draft copied and queued for approval' : 'Draft ready in composer');
      setTimeout(() => setDraftNotice(''), 2500);
      setTimeout(() => textareaRef.current?.focus(), 0);
    } catch (err) {
      const msg = err?.message || 'AI reply draft failed';
      if (msg.toLowerCase().includes('openai')) {
        alert('Please configure an OpenAI API key first.');
      } else {
        alert(msg);
      }
      console.error(err);
    } finally {
      setDraftingReply(false);
    }
  };

  const handleConnectLinkedIn = async () => {
    try {
      setLinkedinConnecting(true);
      setLinkedinConnectError('');
      const { link } = await linkedin.getConnectLink();
      if (!link) throw new Error('LinkedIn connection link was not returned.');
      window.location.href = link;
    } catch (err) {
      console.error(err);
      setLinkedinConnectError(err.message || 'Could not start the LinkedIn connection.');
      setLinkedinConnecting(false);
    }
  };

  const insertEmoji = (e) => {
    setNewMessage((prev) => prev + e);
    setEmojiOpen(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      // eslint-disable-next-line no-alert
      alert('File too large (max 25 MB)');
      e.target.value = '';
      return;
    }
    // Stage only — actual upload happens on Send so the user can add text
    // alongside, or remove the file before sending.
    setPendingAttachment(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingAttachment = () => setPendingAttachment(null);

  const handleTogglePriority = async () => {
    if (!selectedConv) return;
    const newPrio = selectedConv.priority ? 0 : 1;
    const updated = await inbox.patch(selectedConv.id, { priority: newPrio });
    setSelectedConv({ ...selectedConv, priority: updated.priority });
    setConversations((prev) => prev.map((c) => (c.id === selectedConv.id ? { ...c, priority: updated.priority } : c)));
    setMenuOpen(false);
  };

  const handleToggleChatAi = async () => {
    if (!selectedConv) return;
    const nextDisabled = !selectedConv.ai_reply_disabled;
    setDraftNotice('');
    const updated = await inbox.patch(selectedConv.id, { ai_reply_disabled: nextDisabled });
    setSelectedConv((prev) => ({ ...prev, ...updated }));
    setConversations((prev) => prev.map((c) => (c.id === selectedConv.id ? { ...c, ...updated } : c)));
    setMenuOpen(false);
  };

  const handleDeleteConv = async () => {
    if (!selectedConv) return;
    if (!window.confirm('Remove this conversation from your inbox? The LinkedIn thread is untouched.')) return;
    await inbox.remove(selectedConv.id);
    setConversations((prev) => prev.filter((c) => c.id !== selectedConv.id));
    setSelectedConv(null);
    setMenuOpen(false);
  };

  const handleSendToLead = async () => {
    if (!selectedConv) return;
    try {
      const res = await inbox.toLead(selectedConv.id);
      alert(res.created ? 'Lead created from this conversation.' : 'Linked to existing lead.');
    } catch (err) {
      console.error(err);
    }
    setMenuOpen(false);
  };

  const handleAddTag = async () => {
    if (!selectedConv || !tagInput.trim()) return;
    const existing = selectedConv.tags || [];
    const next = Array.from(new Set([...existing, tagInput.trim()])).slice(0, 10);
    const updated = await inbox.patch(selectedConv.id, { tags: next });
    setSelectedConv({ ...selectedConv, tags: updated.tags });
    setConversations((prev) => prev.map((c) => (c.id === selectedConv.id ? { ...c, tags: updated.tags } : c)));
    setTagInput('');
  };

  const handleRemoveTag = async (t) => {
    if (!selectedConv) return;
    const next = (selectedConv.tags || []).filter((x) => x !== t);
    const updated = await inbox.patch(selectedConv.id, { tags: next });
    setSelectedConv({ ...selectedConv, tags: updated.tags });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = parseApiDate(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 7 * 86400000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString(getIntlLocale(), { month: 'short', day: 'numeric' });
  };

  if (!linkedInConnected) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#E6FAF9] rounded-3xl flex items-center justify-center mx-auto mb-6">
            <Link2 className="w-8 h-8 text-[#0B1D3A]" />
          </div>
          <h2 className="text-xl font-semibold text-[#0B1D3A] mb-2">Connect your LinkedIn</h2>
          <p className="text-[#6B7280] mb-6">Connect your LinkedIn account to start syncing messages.</p>
          {linkedinConnectError && (
            <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {linkedinConnectError}
            </p>
          )}
          <button
            onClick={handleConnectLinkedIn}
            disabled={linkedinConnecting}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#12C8C6] hover:bg-[#0FB3B1] text-white font-medium rounded-xl disabled:opacity-60"
            data-testid="connect-linkedin-btn"
          >
            {linkedinConnecting && <Loader2 className="h-4 w-4 animate-spin" />}
            {linkedinConnecting ? 'Starting connection...' : 'Connect LinkedIn'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page-shell">
      <header className="h-14 border-b flex items-center gap-3 px-6">
        <h1 className="text-lg font-semibold whitespace-nowrap">Inbox</h1>
        <div className="flex-1 max-w-md mx-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations…"
              className="w-full pl-9 pr-3 py-1.5 bg-white border border-[#D7DCE4] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#12C8C6]/20"
              data-testid="inbox-top-search"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSync(true)}
            disabled={syncing}
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-xs text-[#6B00B3] hover:bg-[#F0E6FF] rounded-lg font-medium"
            title="Fetches your complete LinkedIn history (may take several minutes)"
            data-testid="deep-sync-btn"
          >
            Full history sync
          </button>
          <button
            onClick={() => handleSync(false)}
            disabled={syncing}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#6B7280] hover:bg-[#E6FAF9] rounded-xl"
            data-testid="sync-btn"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync'}
          </button>
        </div>
      </header>
      {syncNotice && (
        <div className="border-b bg-amber-50 px-6 py-2 text-xs text-amber-900" data-testid="sync-notice">
          {syncNotice}
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Conversation list */}
        <div className={`${selectedConv ? 'hidden md:flex' : 'flex'} w-full md:w-80 border-r border-[#D7DCE4] flex-col bg-[#F7F8F6]`}>
          <div className="p-3 space-y-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-[#D7DCE4] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#12C8C6]/20"
                data-testid="inbox-search"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'unread', 'needs_reply'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-xl ${
                    filter === f ? 'bg-[#12C8C6] text-white' : 'bg-white text-[#6B7280] border border-[#D7DCE4] hover:bg-[#E6FAF9]'
                  }`}
                  data-testid={`filter-${f}`}
                >
                  {f === 'all' ? 'All' : f === 'unread' ? 'Unread' : 'Needs Reply'}
                </button>
              ))}
            </div>
          </div>

          <div ref={listRef} onScroll={onListScroll} className="flex-1 overflow-y-auto" data-testid="conversation-list">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 px-4">
                <p className="text-[#6B7280] text-sm">No conversations yet</p>
              </div>
            ) : (
              <>
                {conversations.map((conv) => (
                  <ConversationItem
                    key={conv.id}
                    conv={conv}
                    selected={selectedConv?.id === conv.id}
                    onClick={() => setSelectedConv(conv)}
                    formatTime={formatTime}
                  />
                ))}
                {loadingMore && (
                  <div className="flex items-center justify-center py-4" data-testid="list-loading-more">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                )}
                {!hasMore && conversations.length > 0 && (
                  <p className="text-center text-xs text-gray-400 py-4">All conversations loaded</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Message area */}
        <div className={`${selectedConv ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 flex-col bg-white`}>
          {selectedConv ? (
            <>
              <div className="h-14 border-b border-[#D7DCE4] flex items-center justify-between px-4 sm:px-6">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedConv(null)}
                    className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#D7DCE4] bg-white text-[#6B7280]"
                    aria-label="Back to conversations"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <Avatar name={selectedConv.lead_name} picture={selectedConv.lead_picture} size="sm" />
                  <div>
                    <p className="text-sm font-medium">{selectedConv.lead_name}</p>
                    {selectedConv.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {selectedConv.tags.map((t) => (
                          <span key={t} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" data-testid={`tag-${t}`}>
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedConv.priority ? (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-md bg-[#FFF4E5] text-[#B35900]" data-testid="priority-badge">
                      <Star className="w-3 h-3 fill-current" /> Priority
                    </span>
                  ) : null}
                  {autoReplyEnabled && selectedConv.ai_reply_disabled ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-md bg-[#F0F0EF] text-[#6B6B6B]" data-testid="chat-ai-off-badge">
                      <Sparkles className="w-3 h-3" /> AI off
                    </span>
                  ) : null}
                </div>
                <div className="relative" ref={menuRef}>
                  <button
                    className="p-2 hover:bg-[#E6FAF9] rounded-xl"
                    onClick={() => setMenuOpen((o) => !o)}
                    data-testid="conv-menu-btn"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                  >
                    <MoreHorizontal className="w-5 h-5 text-[#6B7280]" />
                  </button>
                  {menuOpen && (
                    <div
                      className="absolute right-0 mt-2 w-56 rounded-2xl border border-[#D7DCE4] bg-white shadow-lg z-20 py-1"
                      data-testid="conv-menu"
                      role="menu"
                    >
                      <button onClick={() => { setTagModalOpen(true); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#191919] hover:bg-[#F7F7F5] text-left" data-testid="menu-tags">
                        <Tag className="w-4 h-4" /> Manage tags
                      </button>
                      <button onClick={handleTogglePriority} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#191919] hover:bg-[#F7F7F5] text-left" data-testid="menu-priority">
                        <Star className={`w-4 h-4 ${selectedConv.priority ? 'fill-[#B35900] text-[#B35900]' : ''}`} />
                        {selectedConv.priority ? 'Remove priority' : 'Prioritize'}
                      </button>
                      <button onClick={handleToggleChatAi} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#191919] hover:bg-[#F7F7F5] text-left" data-testid="menu-ai-reply-toggle">
                        <Sparkles className={`w-4 h-4 ${selectedConv.ai_reply_disabled ? 'text-[#9B9B9B]' : 'text-[#1E7F4E]'}`} />
                        {selectedConv.ai_reply_disabled ? 'Enable AI reply' : 'Disable AI reply'}
                      </button>
                      <button onClick={handleSendToLead} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#191919] hover:bg-[#F7F7F5] text-left" data-testid="menu-to-lead">
                        <UserPlus className="w-4 h-4" /> Send to Leads
                      </button>
                      <div className="my-1 border-t border-[#F0F0EF]" />
                      <button onClick={handleDeleteConv} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#CE3636] hover:bg-[#FFEBE9] text-left" data-testid="menu-delete">
                        <Trash2 className="w-4 h-4" /> Delete from inbox
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-[#FBFBFA]">
                {messagesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-[#6B7280]">No messages yet</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <MessageBubble
                      key={msg.id}
                      msg={msg}
                      conv={selectedConv}
                      currentUser={user}
                      formatTime={formatTime}
                      onOpenAttachment={setViewerAttachment}
                    />
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="border-t p-4 bg-white">
                <div className="flex items-end gap-3">
                  <div className="flex-1 bg-[#F7F8F6] rounded-2xl border border-[#D7DCE4] focus-within:ring-2 focus-within:ring-[#12C8C6]/20">
                    {pendingAttachment && (
                      <div className="px-3 pt-3" data-testid="pending-attachment">
                        <div className="inline-flex items-center gap-2 max-w-full bg-white border border-[#CFCFCF] rounded-lg px-2.5 py-1.5 text-xs">
                          <Paperclip className="w-3.5 h-3.5 shrink-0 opacity-70" />
                          <span className="truncate font-medium text-[#191919]" title={pendingAttachment.name}>
                            {pendingAttachment.name}
                          </span>
                          <span className="text-[#9B9B9B] shrink-0">
                            · {Math.max(1, Math.round(pendingAttachment.size / 1024))} KB
                          </span>
                          <button
                            onClick={removePendingAttachment}
                            className="ml-1 p-0.5 rounded hover:bg-[#F0F0EF]"
                            title="Remove attachment"
                            data-testid="pending-attachment-remove"
                          >
                            <X className="w-3.5 h-3.5 text-[#6B6B6B]" />
                          </button>
                        </div>
                      </div>
                    )}
                    <textarea
                      ref={textareaRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      placeholder={pendingAttachment ? 'Add a message (optional)…' : 'Type a message...'}
                      rows={1}
                      className="w-full px-4 py-3 bg-transparent resize-none focus:outline-none text-sm"
                      data-testid="message-input"
                    />
                    <div className="flex items-center justify-between px-3 pb-2">
                      <div className="flex items-center gap-1">
                        <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} data-testid="attach-input" />
                        <button onClick={() => fileInputRef.current?.click()} className="p-1.5 hover:bg-gray-200 rounded-lg" title="Attach file" data-testid="attach-btn">
                          <Paperclip className="w-4 h-4 text-[#6B7280]" />
                        </button>
                        <div className="relative" ref={emojiRef}>
                          <button onClick={() => setEmojiOpen((o) => !o)} className="p-1.5 hover:bg-gray-200 rounded-lg" title="Add emoji" data-testid="emoji-btn">
                            <Smile className="w-4 h-4 text-[#6B7280]" />
                          </button>
                          {emojiOpen && (
                            <div className="absolute bottom-full mb-2 left-0 w-56 p-2 rounded-2xl border border-[#D7DCE4] bg-white shadow-lg z-20 grid grid-cols-6 gap-1" data-testid="emoji-picker">
                              {EMOJIS.map((e) => (
                                <button key={e} onClick={() => insertEmoji(e)} className="text-lg hover:bg-[#F7F7F5] rounded p-1">
                                  {e}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleGenerateReplyDraft}
                          disabled={!selectedConv || draftingReply}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-[#E6F4EA] text-[#1E7F4E] hover:bg-[#D6EDDF] disabled:opacity-50"
                          title="Generate an AI reply draft from recent context"
                          data-testid="ai-reply-draft-btn"
                        >
                          {draftingReply ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          Draft reply
                        </button>
                        {draftNotice && (
                          <span className="hidden sm:inline text-[11px] text-[#1E7F4E]" data-testid="draft-notice">
                            {draftNotice}
                          </span>
                        )}
                        {/* Purple rewrite button */}
                        <button
                          onClick={handleRewrite}
                          disabled={!newMessage.trim() || rewriting}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-[#EADDFF] text-[#5A3EA8] hover:bg-[#DEC9FA] disabled:opacity-50"
                          title="Rewrite more professionally"
                          data-testid="rewrite-btn"
                        >
                          {rewriting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                          Rewrite
                        </button>
                        <button
                          type="button"
                          onClick={handleToggleChatAi}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
                            autoReplyEnabled && !selectedConv.ai_reply_disabled ? 'bg-[#E6F4EA] text-[#1E7F4E]' : 'bg-[#F0F0EF] text-[#9B9B9B]'
                          } hover:bg-[#E3E2E0]`}
                          title={selectedConv.ai_reply_disabled ? 'Enable AI reply for this chat' : 'Disable AI reply for this chat'}
                          data-testid="autoreply-indicator"
                        >
                          <Sparkles className="w-3 h-3" />
                          AI {autoReplyEnabled && !selectedConv.ai_reply_disabled ? 'On' : 'Off'}
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleSend}
                    disabled={(!newMessage.trim() && !pendingAttachment) || sending}
                    className="p-3 bg-[#12C8C6] hover:bg-[#0FB3B1] text-white rounded-xl disabled:opacity-50"
                    data-testid="send-btn"
                  >
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#E6FAF9] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <InboxIcon className="w-8 h-8 text-[#0B1D3A]" />
                </div>
                <p className="text-[#6B7280]">Select a conversation to start</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tag modal */}
      {tagModalOpen && selectedConv && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setTagModalOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 p-5" onClick={(e) => e.stopPropagation()} data-testid="tag-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold">Tags</h3>
              <button onClick={() => setTagModalOpen(false)} className="p-1 hover:bg-[#F0F0EF] rounded">
                <X className="w-4 h-4 text-[#6B6B6B]" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-3 min-h-8">
              {(selectedConv.tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#F0F0EF] text-sm text-[#191919]">
                  {t}
                  <button onClick={() => handleRemoveTag(t)} className="text-[#6B6B6B] hover:text-[#CE3636]">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {(selectedConv.tags || []).length === 0 && (
                <p className="text-xs text-[#6B6B6B]">No tags yet.</p>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Add a tag and press Enter"
                className="flex-1 px-3 py-2 border border-[#D7DCE4] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#12C8C6]/20"
                data-testid="tag-input"
              />
              <button onClick={handleAddTag} className="px-3 py-2 bg-[#0A0A0A] text-white rounded-lg text-sm" data-testid="tag-add-btn">
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      <FileViewerModal attachment={viewerAttachment} onClose={() => setViewerAttachment(null)} />
    </div>
  );
}

function Avatar({ name, picture, size = 'md' }) {
  const sizes = { xs: 'w-6 h-6', sm: 'w-8 h-8', md: 'w-10 h-10', lg: 'w-12 h-12' };
  const textSizes = { xs: 'text-[9px]', sm: 'text-[11px]', md: 'text-xs', lg: 'text-sm' };
  const [imgFailed, setImgFailed] = React.useState(false);
  // LinkedIn CDN URLs are time-signed and occasionally hot-link blocked.
  // Always route them through our backend image proxy so the picture is
  // served from our own origin (no CORS / Referer / mixed-content issues).
  const resolvedSrc = React.useMemo(() => {
    if (!picture) return null;
    try {
      const isLicdn = /(^|\.)licdn\.com$/i.test(new URL(picture).hostname);
      return isLicdn ? inbox.imageProxyUrl(picture) : picture;
    } catch {
      return picture;
    }
  }, [picture]);

  if (resolvedSrc && !imgFailed) {
    return (
      <img
        src={resolvedSrc}
        alt={name || ''}
        onError={() => setImgFailed(true)}
        referrerPolicy="no-referrer"
        loading="lazy"
        className={`${sizes[size]} rounded-full object-cover shrink-0 bg-[#EEF2F7]`}
      />
    );
  }
  // Color-coded fallback so contacts are visually distinct
  const palette = ['#FFE5D9', '#DCFCE7', '#EADDFF', '#FEF3C7', '#FCE7F3', '#E0F2FE', '#F5D9B0', '#E6F4EA', '#FFE4E6', '#E0E7FF'];
  const colors = ['#9A3412', '#166534', '#5A3EA8', '#854D0E', '#9D174D', '#075985', '#B35900', '#1E7F4E', '#9F1239', '#3730A3'];
  const safeName = (name || '?').trim();
  const parts = safeName.split(/\s+/).filter(Boolean);
  const initials = (parts.length >= 2
    ? parts[0][0] + parts[parts.length - 1][0]
    : (parts[0] || '?')[0]).toUpperCase();
  let hash = 0;
  for (let i = 0; i < safeName.length; i++) hash = (hash * 31 + safeName.charCodeAt(i)) >>> 0;
  const bg = palette[hash % palette.length];
  const fg = colors[hash % colors.length];
  return (
    <div
      className={`${sizes[size]} rounded-full flex items-center justify-center shrink-0`}
      style={{ backgroundColor: bg }}
    >
      <span className={`${textSizes[size]} font-semibold tracking-wide`} style={{ color: fg }}>
        {initials}
      </span>
    </div>
  );
}

function ConversationItem({ conv, selected, onClick, formatTime }) {
  return (
    <button
      onClick={onClick}
      className={`w-full p-4 text-left border-b border-[#E4E8EF] hover:bg-[#EDF7F7] transition-colors ${selected ? 'bg-[#E6FAF9]' : ''}`}
      data-testid={`conversation-${conv.id}`}
    >
      <div className="flex items-start gap-3">
        <Avatar name={conv.lead_name} picture={conv.lead_picture} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className={`text-sm truncate ${conv.unread_count > 0 ? 'font-semibold' : ''}`}>
              {conv.priority ? <Star className="inline w-3 h-3 mr-1 fill-[#B35900] text-[#B35900]" /> : null}
              {conv.lead_name || 'Unknown'}
            </span>
            <span className="text-xs text-gray-400 ml-2">{formatTime(conv.last_message_at || conv.updated_at)}</span>
          </div>
          <p className={`text-xs truncate ${conv.unread_count > 0 ? 'text-[#0B1D3A]' : 'text-[#6B7280]'}`}>
            {conv.last_message || 'No messages'}
          </p>
          <div className="flex items-center gap-2 mt-1">
            {conv.unread_count > 0 && (
              <span className="px-1.5 py-0.5 bg-[#0B1D3A] text-white text-xs rounded-full">{conv.unread_count}</span>
            )}
            {conv.needs_reply && (
              <span className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />Needs reply
              </span>
            )}
            {conv.ai_reply_disabled && (
              <span className="text-xs text-[#6B7280] flex items-center gap-1">
                <Sparkles className="w-3 h-3" />AI off
              </span>
            )}
            {conv.tags?.slice(0, 2).map((t) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-[#D7DCE4] text-[#6B7280]">{t}</span>
            ))}
          </div>
        </div>
      </div>
    </button>
  );
}

function MessageBubble({ msg, conv, currentUser, formatTime, onOpenAttachment }) {
  const isOut = msg.direction === 'out';
  const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
  const hasAttachments = attachments.length > 0;

  // Suppress the auto-generated "[Attachment] filename" placeholder when we
  // have a real attachment record to render instead.
  const displayText = hasAttachments && /^\[Attachment\]/i.test(msg.text || '')
    ? ''
    : msg.text;

  if (isOut) {
    return (
      <div className="flex items-end justify-end gap-2" data-testid="msg-out">
        <div className="max-w-[70%] px-4 py-2.5 rounded-2xl rounded-br-md bg-[#CCFFFF] text-[#0F3D3D] border border-[#A0E8E8] shadow-sm">
          {displayText && <p className="text-sm whitespace-pre-wrap">{displayText}</p>}
          {hasAttachments && (
            <AttachmentList attachments={attachments} variant="out" onOpen={onOpenAttachment} />
          )}
          <p className="text-[11px] mt-1 text-[#0F3D3D]/60 text-right">{formatTime(msg.created_at)}</p>
        </div>
        <div className="shrink-0">
          <Avatar name={currentUser?.name || 'You'} picture={currentUser?.avatar} size="xs" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-end gap-2" data-testid="msg-in">
      <div className="shrink-0">
        <Avatar name={conv?.lead_name} picture={conv?.lead_picture} size="xs" />
      </div>
      <div className="max-w-[70%] px-4 py-2.5 rounded-2xl rounded-bl-md bg-[#E0E0E0] text-[#191919] border border-[#CFCFCF]">
        {displayText && <p className="text-sm whitespace-pre-wrap">{displayText}</p>}
        {hasAttachments && (
          <AttachmentList attachments={attachments} variant="in" onOpen={onOpenAttachment} />
        )}
        <p className="text-[11px] mt-1 text-[#6B6B6B]">{formatTime(msg.created_at)}</p>
      </div>
    </div>
  );
}

function AttachmentList({ attachments, variant, onOpen }) {
  const isOut = variant === 'out';
  return (
    <div className="mt-2 flex flex-col gap-1.5" data-testid="msg-attachments">
      {attachments.map((att, idx) => {
        const name = att?.name || att?.file_name || att?.filename || `file-${idx + 1}`;
        const type = att?.type || att?.mimetype || '';
        const isImage = String(type).startsWith('image/');
        const Icon = isImage ? ImageIcon : Paperclip;
        return (
          <button
            key={`${name}-${idx}`}
            onClick={() => onOpen?.(att)}
            className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-left transition-colors hover:opacity-90 ${
              isOut
                ? 'bg-white/60 border border-[#A0E8E8] text-[#0F3D3D] hover:bg-white'
                : 'bg-white border border-[#CFCFCF] text-[#191919] hover:bg-[#F7F7F5]'
            }`}
            data-testid="msg-attachment-item"
          >
            <Icon className="w-4 h-4 shrink-0 opacity-70" />
            <span className="truncate font-medium" title={name}>{name}</span>
            {type && <span className="opacity-60 shrink-0">· {type.split('/').pop()}</span>}
          </button>
        );
      })}
    </div>
  );
}

function FileViewerModal({ attachment, onClose }) {
  if (!attachment) return null;
  const name = attachment.name || attachment.file_name || attachment.filename || 'file';
  const type = attachment.type || attachment.mimetype || '';
  // Every attachment (locally uploaded OR fetched from Unipile) is now
  // streamed through /api/inbox/attachments/:id by the backend, so we
  // always have a previewable URL regardless of source.
  const url = attachment.id ? inbox.attachmentUrl(attachment.id) : null;
  const downloadUrl = attachment.id
    ? inbox.attachmentUrl(attachment.id, { download: true })
    : null;
  const ext = (name.split('.').pop() || '').toLowerCase();
  const isImage = String(type).startsWith('image/') || ['png','jpg','jpeg','gif','webp','svg'].includes(ext);
  const isPdf = type === 'application/pdf' || ext === 'pdf';
  const isText = String(type).startsWith('text/') || ['txt','md','csv','log','json'].includes(ext);
  const isVideo = String(type).startsWith('video/') || ['mp4','webm','mov'].includes(ext);
  const isAudio = String(type).startsWith('audio/') || ['mp3','wav','ogg','m4a'].includes(ext);

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="file-viewer-modal">
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#D7DCE4]">
          <div className="min-w-0 flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-[#6B6B6B] shrink-0" />
            <span className="truncate text-sm font-medium text-[#191919]" title={name}>{name}</span>
            {type && <span className="text-xs text-[#6B6B6B] shrink-0">· {type}</span>}
          </div>
          <div className="flex items-center gap-2">
            {downloadUrl && (
              <a
                href={downloadUrl}
                download={name}
                className="text-xs px-2 py-1 rounded-md border border-[#D7DCE4] hover:bg-[#E6FAF9]"
                data-testid="file-viewer-download"
              >
                Download
              </a>
            )}
            <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F0EF]" data-testid="file-viewer-close">
              <X className="w-4 h-4 text-[#6B6B6B]" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-[#FBFBFA]">
          {!url ? (
            <div className="h-full flex items-center justify-center p-10 text-center">
              <div className="text-sm text-[#6B6B6B] max-w-md">
                <p className="font-medium text-[#191919] mb-1">Preview not available</p>
              </div>
            </div>
          ) : isImage ? (
            <img src={url} alt={name} className="max-w-full max-h-[75vh] mx-auto" />
          ) : isPdf ? (
            <iframe src={url} title={name} className="w-full h-[75vh]" />
          ) : isVideo ? (
            <video src={url} controls className="max-w-full max-h-[75vh] mx-auto" />
          ) : isAudio ? (
            <div className="p-10 flex items-center justify-center"><audio src={url} controls /></div>
          ) : isText ? (
            <iframe src={url} title={name} className="w-full h-[75vh] bg-white" />
          ) : (
            <div className="h-full flex items-center justify-center p-10 text-center">
              <div className="text-sm text-[#6B6B6B]">
                <p className="font-medium text-[#191919] mb-1">No inline preview for this file type</p>
                <p>Use the Download button above to save it locally.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
